/* Canvello v8 — the fitting room (Task 3.2). <cv-size> in
 * sections/cv-size.liquid; Canvello v8:L1870-1970 as plain DOM code.
 *
 * State: the placed size (the checked radio), a previewed one (pointer over
 * a step), compare on or off, and the piece picked per size. Whatever is
 * shown — the preview, else the placed size — stands on the seat with the
 * sizes either side by face area (3-up) or alone (solo), and its panel shows.
 *
 * Ladder: a radio group with one tab stop. Click places; ←/→ step and place
 * (mirrored in RTL, where the ladder runs right to left), ↑/↓ too, Home and
 * End. Pointer hover previews and leaving restores; the ladder never
 * previews on focus, since focus already places.
 *
 * The compare line ("16″ −36% · 20″ selected · 24″ +44% face") goes to a
 * polite live region only from the first change on, so the page does not
 * announce it on load. Percentages are formatted for the page's language.
 * Theme editor: selecting a size block places it.
 */
(() => {
  if (customElements.get('cv-size')) return;

  // v8:L1884, as % of the 720 x 348 plate.
  const GAP = (10 / 720) * 100;
  const PX_W = 0.96722;
  const PX_H = 2.00115;
  const SEAT = 45.402;
  const ROT = [-2.4, -0.8, 2.2];

  // Sizes and percentages are isolated left to right, so "16″ −36%" keeps its
  // order inside an Arabic sentence.
  const iso = (s) => `\u2066${s}\u2069`;
  const fill = (tpl, map) => tpl.replace(/%(\w+)%/g, (m, k) => (k in map ? map[k] : m));

  class CVSize extends HTMLElement {
    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('.cv-size__step'));
      if (!this.steps.length) return;
      this.stage = this.querySelector('.cv-size__stage');
      this.pieces = Array.from(this.querySelectorAll('.cv-size__piece'));
      this.lines = Array.from(this.querySelectorAll('.cv-size__line'));
      this.panels = Array.from(this.querySelectorAll('.cv-size__panel'));
      this.toggle = this.querySelector('[data-cv-size-compare]');
      this.line = this.querySelector('[data-cv-size-line]');
      this.index = this.querySelector('[data-cv-size-index]');

      this.sizes = this.steps.map((s) => {
        const w = parseFloat(s.style.getPropertyValue('--w'));
        const h = parseFloat(s.style.getPropertyValue('--h'));
        return { w, h, area: w * h, label: s.querySelector('.cv-size__step-label').textContent.trim() };
      });
      // Window order for 3-up: by face area, so a lumbar stands between the
      // squares either side of it.
      this.byArea = this.sizes.map((_, i) => i).sort((a, b) => this.sizes[a].area - this.sizes[b].area);

      this.pick = Math.max(0, this.steps.findIndex((s) => s.getAttribute('aria-checked') === 'true'));
      this.hover = null;
      this.compare = this.dataset.compare !== 'false';
      // Latin digits, as every other number on the page (cv-number).
      const lang = document.documentElement.lang || 'en';
      this.pct = new Intl.NumberFormat(`${lang}-u-nu-latn`, {
        style: 'percent',
        maximumFractionDigits: 0,
        signDisplay: 'exceptZero',
      });

      this.steps.forEach((step, i) => {
        step.addEventListener('click', () => this.place(i));
        step.addEventListener('keydown', (e) => this.key(e, i));
        step.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.hover = i;
          this.render();
        });
        step.addEventListener('pointerleave', () => {
          if (this.hover !== i) return;
          this.hover = null;
          this.render();
        });
      });

      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          this.compare = !this.compare;
          this.render();
        });
      }

      this.panels.forEach((panel) => {
        panel.querySelectorAll('.cv-size__swatch').forEach((sw) => sw.addEventListener('click', () => this.cloth(panel, sw)));
      });

      this.onBlock = (e) => {
        const i = this.steps.indexOf(e.target);
        if (i > -1) this.place(i);
      };
      document.addEventListener('shopify:block:select', this.onBlock);

      this.render();
      this.stage.setAttribute('data-laid', '');
      // Live only from here on: the first line is not announced on load.
      if (this.line) this.line.setAttribute('aria-live', 'polite');
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlock);
    }

    key(e, i) {
      const rtl = getComputedStyle(this).direction === 'rtl';
      const n = this.steps.length;
      const step = {
        ArrowRight: rtl ? -1 : 1,
        ArrowLeft: rtl ? 1 : -1,
        ArrowDown: 1,
        ArrowUp: -1,
      }[e.key];
      let next = null;
      if (step) next = (i + step + n) % n;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = n - 1;
      if (next == null) return;
      e.preventDefault();
      this.place(next);
      this.steps[next].focus();
    }

    place(i) {
      this.pick = i;
      this.hover = null;
      this.render();
    }

    // A piece from the panel's swatches stands in for this size on the stage.
    cloth(panel, sw) {
      const key = +panel.dataset.key;
      const tpl = sw.querySelector('template');
      const face = this.pieces[key] && this.pieces[key].querySelector('[data-cv-size-face]');
      if (tpl && face) face.replaceChildren(tpl.content.cloneNode(true));
      panel.querySelectorAll('.cv-size__swatch').forEach((b) => b.setAttribute('aria-pressed', String(b === sw)));
      const name = panel.querySelector('[data-cv-size-name]');
      if (name) {
        name.textContent = sw.dataset.title;
        // A cloth (v8's cut-outs) has no page; its name is plain text.
        if (sw.dataset.url && name.tagName === 'A') name.href = sw.dataset.url;
      }
      if (key !== this.pick) this.place(key);
    }

    trio(cur) {
      if (!this.compare || this.sizes.length < 2) return [cur];
      const at = this.byArea.indexOf(cur);
      const s = Math.max(0, Math.min(this.byArea.length - 3, at - 1));
      return this.byArea.slice(s, s + 3).sort((a, b) => this.byArea.indexOf(a) - this.byArea.indexOf(b));
    }

    render() {
      const cur = this.hover ?? this.pick;
      const shown = this.trio(cur);
      const widths = shown.map((k) => this.sizes[k].w * PX_W);
      let x = 50 - (widths.reduce((a, b) => a + b, 0) + GAP * (shown.length - 1)) / 2;
      const at = new Map();
      shown.forEach((k, i) => {
        at.set(k, { x, rot: k === cur ? -1 : ROT[shown.length === 3 ? i : 1], i });
        x += widths[i] + GAP;
      });

      this.pieces.forEach((piece, k) => {
        const spot = at.get(k);
        piece.toggleAttribute('data-shown', !!spot);
        piece.toggleAttribute('data-on', k === cur);
        if (spot) {
          piece.style.setProperty('--x', `${spot.x.toFixed(3)}%`);
          piece.style.setProperty('--r', `${spot.rot}deg`);
        }
      });
      this.lines.forEach((line, k) => {
        const spot = at.get(k);
        line.toggleAttribute('data-shown', !!spot);
        line.toggleAttribute('data-on', k === cur);
        // Labels alternate ends so neighbouring heights never collide.
        line.toggleAttribute('data-end', !!spot && spot.i % 2 === 1);
      });

      this.steps.forEach((step, k) => {
        const on = k === this.pick;
        step.setAttribute('aria-checked', String(on));
        step.tabIndex = on ? 0 : -1;
        step.toggleAttribute('data-show', k === cur);
        step.toggleAttribute('data-cmp', this.compare && k !== cur && at.has(k));
      });

      this.panels.forEach((panel, k) => {
        if (k === cur && panel.hidden && window.CVMotion) {
          panel.hidden = false;
          window.CVMotion.scan(panel);
        }
        panel.hidden = k !== cur;
      });

      if (this.toggle) this.toggle.setAttribute('aria-pressed', String(this.compare));

      if (this.index) {
        const pad = (n) => String(n).padStart(2, '0');
        this.index.textContent = fill(this.dataset.tIndex, { n: pad(cur + 1), total: pad(this.sizes.length) });
      }

      if (this.line) {
        const a0 = this.sizes[cur].area;
        const text =
          shown.length > 1
            ? fill(this.dataset.tLine, {
                list: shown
                  .map((k) =>
                    k === cur
                      ? fill(this.dataset.tSelected, { size: iso(this.sizes[k].label) })
                      : fill(this.dataset.tDelta, { size: iso(this.sizes[k].label), delta: iso(this.pct.format(this.sizes[k].area / a0 - 1)) })
                  )
                  .join(' · '),
              })
            : fill(this.dataset.tAlone, { size: iso(this.sizes[cur].label) });
        this.line.textContent = text;
      }
    }
  }

  customElements.define('cv-size', CVSize);
})();
